import React, { useState, useEffect, useCallback, useRef } from 'react';
import Timer from './Timer';
import { logoutUser } from "../src/features/user/userSlice";
import { clearExam } from "./features/exam/examSlice";
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchExamById,
  setSelectedAnswerForQuestion,
} from './features/exam/examSlice.js';
import QuestionBubble from './QuestionBubble.jsx';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import { clearExamVerified } from "../src/features/user/userSlice";
import axios from 'axios';
import { API_URL } from './constants.js';
import styles from './Exam.module.css';
import { First } from 'react-bootstrap/esm/PageItem.js';

export default function Exam() {
  const {
    ExamPassingGradeThreshhold,
    ExamDateBarrier,
    ExamTimeAllowed,
    loading,
    error,
  } = useSelector(state => state.exam);

  const dispatch    = useDispatch();
  const navigate    = useNavigate();
  const location    = useLocation();
  const examCodeState = location.state;

  const questions        = useSelector(state => state.exam.examquestions);
  const examID           = useSelector(state => state.user.exams.ExamID);
  const courseIDfromUser = useSelector(state => state.user.exams.CourseID);
  const Cert             = useSelector(state => state.exam.ExamName);
  const userID           = useSelector(state => state.user.user.CertUserID);
  const FirstName        = useSelector(state => state.user.user.FirstName);
  const LastName         = useSelector(state => state.user.user.LastName);
  const sections         = useSelector(state => state.exam.sections);
  const examPopulated = useSelector(state => state.exam.examPopulated);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showScore, setShowScore]     = useState(false);
  const [skipped, setSkipped]         = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // prevents double-submit
  const [resultMessage, setResultMessage] = useState('');
  const [Score, setScore]             = useState(0);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [isCertLoading, setIsCertLoading] = useState(false);

  // ─── Load exam on mount ────────────────────────────────────────────────────
const hasFetched = useRef(false);

useEffect(() => {
  if (!hasFetched.current && examID && courseIDfromUser) {
    hasFetched.current = true;
    dispatch(fetchExamById(examID));
  }
}, [examID, courseIDfromUser, dispatch]);

  useEffect(() => {
  if (!courseIDfromUser && !loading) {
    navigate('/examentry');
  }
}, [courseIDfromUser, loading, navigate]);

  // ─── Warn on page refresh ──────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = 'Warning! Refreshing the page will restart the entire exam.';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // ─── Clear state and navigate home ────────────────────────────────────────
  const clearAndExit = useCallback(() => {
    dispatch(clearExam());
    //dispatch(logoutUser());
    dispatch(clearExamVerified());
    navigate('/');
  }, [dispatch, navigate]);

  // ─── Mark exam code as used ────────────────────────────────────────────────
  const setExamCodeStatus = useCallback(async (code) => {
    if (!code) return;
    try {
      const resp = await axios.post(`${API_URL}/examcode/updatestatus/true`, {
        examcodeid: code,
      });
      console.log('Exam code status updated:', resp.data);
      dispatch(clearExamVerified());
    } catch (ex) {
      console.error('Failed to update exam code status:', ex);
    }
  }, [dispatch]);

  // ─── Score calculation (pure — no side effects) ───────────────────────────
  const calculateScore = useCallback(() => {
    const totalPossibleScore = questions
      .map(q => sections.find(s => s.SectionID === q.SectionID)?.Score ?? 0)
      .reduce((acc, val) => acc + val, 0);

    if (totalPossibleScore === 0) return 0;

    const earnedPoints = questions
      .map(q => {
        if (q.selectedAnswer == null) return 0;
        const correctAnswer = q.examanswers.find(a => a.IsCorrect)?.Answer;
        const isCorrect = q.selectedAnswer.Answer === correctAnswer;
        return isCorrect
          ? (sections.find(s => s.SectionID === q.SectionID)?.Score ?? 0)
          : 0;
      })
      .reduce((acc, val) => acc + val, 0);

    return Math.round((earnedPoints / totalPossibleScore) * 100);
  }, [questions, sections]);

  // ─── POST results to server ────────────────────────────────────────────────
  const sendResults = useCallback(async (score) => {
    const certuserexam = {
      UserID:      userID,
      ExamID:      examID,
      ExamAttempt: 1,
      ExamGrade:   score,
      ExamDate:    new Date(),
    };
    //console.log('User Exam:',certuserexam);
    const response = await axios.post(`${API_URL}/certuserexam`, { certuserexam });
    console.log('Exam score saved:', response.data);
    return response.data;
  }, [userID, examID]);

  // ─── Main submit flow ──────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (isSubmitting || isSubmitted) return;
    setIsSubmitting(true);

    try {
      const score = calculateScore();
      setScore(score);

      // Save results first — wait for confirmation before showing score
      await sendResults(score);
      setIsSubmitted(true);

      // Mark the exam code used (non-blocking — failure shouldn't stop the flow)
      await setExamCodeStatus(examCodeState);

      // Now reveal results
      setShowScore(true);

      if (score >= ExamPassingGradeThreshhold) {
        setResultMessage(
          `Congrats! You have passed this exam with a score of ${score}%.`
        );
      } else {
        setResultMessage(
          `You scored ${score}%. Unfortunately you did not meet the minimum required ` +
          `score of ${ExamPassingGradeThreshhold}% to become certified. ` +
          `You may attempt the exam again in ${ExamDateBarrier} days.`
        );
      }
    } catch (err) {
      console.error('Submission error:', err);
      alert('There was a problem submitting your exam. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    isSubmitted,
    calculateScore,
    sendResults,
    setExamCodeStatus,
    examCodeState,
    ExamPassingGradeThreshhold,
    ExamDateBarrier,
  ]);

  // ─── Proceed to certification ──────────────────────────────────────────────
  // After cert record is added - navigate to cert page, THEN clear on unmount
const addCertRecord = () => {
  // Capture before any state clearing
  const courseID = courseIDfromUser;
  
  let CertDate = new Date();
  try{
  axios.post(`${API_URL}/addusercert`, { FirstName, LastName, CertDate, Cert })
    .then(response => {
      // Clear AFTER we have everything we need
      dispatch(clearExam());
      dispatch(clearExamVerified());
      
      // Use local variable, not Redux selector
      navigate(`/cert-generation/${courseID}`, { state: response.data });
    });
  }
  catch(ex){
    console.log(ex);
  }
};

  // ─── Navigation handlers ───────────────────────────────────────────────────
  const handleBubbleClick = (index) => {
    const newSkipped = questions.filter(
      (q, i) =>
        (q.selectedAnswer == null && i <= index) ||
        skipped.some(s => s.ExamQuestionID === q.ExamQuestionID)
    );
    setSkipped(newSkipped);
    setCurrentQuestionIndex(index);
  };

  const handleAnswerOptionClick = (Answer) => {
    dispatch(
      setSelectedAnswerForQuestion({
        ExamQuestionID: questions[currentQuestionIndex].ExamQuestionID,
        Answer,
      })
    );

    const skippedOtherThanHere = skipped.filter(
      q => q.ExamQuestionID !== questions[currentQuestionIndex].ExamQuestionID
    );
    setSkipped(skippedOtherThanHere);

    const nextQuestion = questions[currentQuestionIndex + 1];
    if (nextQuestion) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else if (skippedOtherThanHere.length > 0) {
      const firstSkippedID = skippedOtherThanHere[0].ExamQuestionID;
      setCurrentQuestionIndex(
        questions.findIndex(q => q.ExamQuestionID === firstSkippedID)
      );
    }
  };

  const handleBackClick = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleNextClick = () => {
    const current = questions[currentQuestionIndex];
    const alreadySkipped = skipped.some(q => q.ExamQuestionID === current.ExamQuestionID);

    if (current.selectedAnswer == null && !alreadySkipped) {
      setSkipped(prev => [...prev, current]);
    }

    if (currentQuestionIndex + 1 < questions.length) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  // ─── Section header ────────────────────────────────────────────────────────
  const showHeaderSection = () => {
    try {
      const currentSection = sections.find(
        s => s.SectionID === questions[currentQuestionIndex]?.SectionID
      );
      return currentSection
        ? `${currentSection.Name} (${currentSection.Score} pts)`
        : '';
    } catch {
      return '';
    }
  };

  const allAnswered = questions.every(q => q.selectedAnswer != null);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={styles.examLayout}>

      {/* Top Bar */}
      <div className={styles.examTopbar}>
        <div className={styles.examTitle}>
          {!isSubmitted && <h2>{showHeaderSection()}</h2>}
        </div>
        <Timer
          examtimeallowed={ExamTimeAllowed}
          timeUp={() => handleSubmit()}
          isSubmitted={isSubmitted}
        />
      </div>

      <div className={styles.examContent}>

        {/* Main Card */}
        <div className={styles.examCard}>
          {showScore ? (
            <div>
              <h2>Results</h2>
              <p>{resultMessage}</p>
            </div>
          ) : (
            <>
              <div className={styles.questionHeader}>
                Question {currentQuestionIndex + 1} / {questions.length}
              </div>

              <div className={styles.questionText}>
                {questions[currentQuestionIndex]?.Question}
              </div>

              <div className={styles.answersGrid}>
                {questions[currentQuestionIndex]?.examanswers?.map(answer => {
                  const isSelected =
                    questions[currentQuestionIndex].selectedAnswer?.Answer ===
                    answer.Answer;
                  return (
                    <button
                      key={answer.Answer}
                      className={`${styles.answerBtn} ${isSelected ? styles.selected : ''}`}
                      onClick={() => handleAnswerOptionClick(answer.Answer)}
                    >
                      {answer.Answer}
                    </button>
                  );
                })}
              </div>

              <div className={styles.navWrapper}>
                <button
                  onClick={handleBackClick}
                  disabled={currentQuestionIndex === 0}
                  className={`${styles.navBtn} ${styles.backBtn}`}
                >
                  ← Back
                </button>
                <button
                  onClick={handleNextClick}
                  disabled={currentQuestionIndex === questions.length - 1}
                  className={`${styles.navBtn} ${styles.nextBtn}`}
                >
                  Next →
                </button>
              </div>
            </>
          )}
        </div>

        {/* Question Bubbles Panel */}
        <div className={`${styles.bubblePanel} ${isPanelCollapsed ? styles.collapsed : ''}`}>
          <div
            className={styles.toggleButton}
            onClick={() => setIsPanelCollapsed(prev => !prev)}
          >
            {isPanelCollapsed ? '>>' : '<<'}
          </div>

          {!isPanelCollapsed && (
            <div className={styles.bubbleGrid}>
              {questions.map((question, index) => (
                <QuestionBubble
                  key={question.ExamQuestionID ?? index}
                  disabled={showScore}
                  onClick={() => handleBubbleClick(index)}
                  skipped={skipped.some(q => q.ExamQuestionID === question.ExamQuestionID)}
                  current={index === currentQuestionIndex}
                  answered={question.selectedAnswer != null}
                  questionNumber={index + 1}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className={styles.examFooter}>
        {!allAnswered && !isSubmitted ? (
          <button
            className={`${styles.btn} ${styles.disabled} ${styles.wide}`}
            disabled
          >
            Answer all questions to submit
          </button>
        ) : (
          <>
            <button
              className={`${styles.btn} ${styles.success}`}
              onClick={handleSubmit}
              disabled={isSubmitted || isSubmitting}
            >
              {isSubmitting ? 'Submitting…' : 'Submit'}
            </button>

            {isSubmitted && (
              Score >= ExamPassingGradeThreshhold ? (
                <button
                  className={`${styles.btn} ${styles.primary}`}
                  onClick={addCertRecord}
                  disabled={isCertLoading}
                >
                  {isCertLoading ? 'Loading…' : 'Proceed to Certification'}
                </button>
              ) : (
                <button
                  className={`${styles.btn} ${styles.danger}`}
                  onClick={clearAndExit}
                >
                  Exit Exam
                </button>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}
